import { initContactFormLabels } from "./modules/contact-form-labels.js";

class FAQAnimationHandler {
  constructor() {
    this.init();
  }

  init() {
    const detailsElements = document.querySelectorAll(
      "details.wp-block-details",
    );
    detailsElements.forEach((details) => this.setupDetailElement(details));
  }

  setupDetailElement(details) {
    const summary = details.querySelector("summary");

    summary.addEventListener("click", (e) => {
      // If it's currently open, we need to handle the closing animation
      if (details.hasAttribute("open")) {
        e.preventDefault(); // Prevent default closing behavior

        // Add closing class for animation
        details.classList.add("closing");

        // Wait for animation to complete, then actually close
        setTimeout(() => {
          details.removeAttribute("open");
          details.classList.remove("closing");
        }, 400); // Match the CSS transition duration
      }
      // If it's closed, let it open naturally (CSS handles the opening animation)
    });

    // Handle keyboard navigation (Enter/Space)
    summary.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        summary.click();
      }
    });
  }

  // Alternative approach for WordPress (if you need to initialize after content loads)
  reinitialize() {
    const detailsElements = document.querySelectorAll(
      "details.wp-block-details",
    );

    detailsElements.forEach((details) => {
      // Skip if already initialized
      if (details.hasAttribute("data-faq-initialized")) return;

      this.setupDetailElement(details);

      // Mark as initialized
      details.setAttribute("data-faq-initialized", "true");
    });
  }
}

document.addEventListener("DOMContentLoaded", () => new FAQAnimationHandler());

class AutoHideHeader {
  constructor() {
    this.header = document.querySelector(".wp-block-template-part");
    this.body = document.body;
    this.lastScrollY = window.scrollY;
    this.scrollThreshold = 10;
    this.headerHeight = this.header.offsetHeight;
    this.isScrolling = false;
    this.isFixed = false;
    this.scrollEndTimer = null;

    window.addEventListener("scroll", this.throttleScroll.bind(this), {
      passive: true,
    });
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  throttleScroll() {
    if (!this.isScrolling) {
      requestAnimationFrame(this.handleScroll.bind(this));
      this.isScrolling = true;
    }

    // Clear existing timer and set new one to detect scroll end
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.handleScrollEnd();
    }, 100);
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollDifference = Math.abs(currentScrollY - this.lastScrollY);

    if (scrollDifference < this.scrollThreshold) {
      this.isScrolling = false;
      return;
    }

    const isAtBottom =
      window.innerHeight + currentScrollY >= document.body.offsetHeight - 10;

    if (currentScrollY <= this.headerHeight) {
      this.setNormalFlow();
    } else if (isAtBottom) {
      this.setFixed();
      this.showHeader();
    } else if (currentScrollY > this.lastScrollY) {
      if (!this.isFixed) {
        this.setFixedHiddenInstant();
      } else {
        this.hideHeader();
      }
    } else {
      this.setFixed();
      this.showHeader();
    }

    this.lastScrollY = currentScrollY;
    this.isScrolling = false;
  }

  handleScrollEnd() {
    // When scrolling ends, ensure state matches current position
    const currentScrollY = window.scrollY;

    if (currentScrollY <= this.headerHeight) {
      // We're at the top, ensure normal flow
      this.setNormalFlow();
    } else {
      // We're scrolled down, ensure fixed state is correct
      if (!this.isFixed) {
        this.setFixed();
      }
    }
  }

  setNormalFlow() {
    this.header.classList.remove(
      "header-fixed",
      "header-hidden",
      "header-visible",
      "header-no-transition",
    );
    this.body.classList.remove("header-is-fixed");
    this.isFixed = false;
  }

  setFixed() {
    if (!this.isFixed) {
      this.header.classList.add("header-fixed");
      this.body.classList.add("header-is-fixed");
      this.isFixed = true;
    }
  }

  setFixedHiddenInstant() {
    // Disable transition temporarily
    this.header.classList.add("header-no-transition");

    // Set hidden state without animation
    this.header.classList.add("header-fixed", "header-hidden");
    this.header.classList.remove("header-visible");
    this.body.classList.add("header-is-fixed");
    this.isFixed = true;

    // Re-enable transitions after a frame
    requestAnimationFrame(() => {
      this.header.classList.remove("header-no-transition");
    });
  }

  showHeader() {
    this.header.classList.remove("header-hidden");
    this.header.classList.add("header-visible");
  }

  hideHeader() {
    this.header.classList.remove("header-visible");
    this.header.classList.add("header-hidden");
  }

  handleResize() {
    this.headerHeight = this.header.offsetHeight;
    this.lastScrollY = window.scrollY;

    // Check state after resize
    this.handleScrollEnd();
  }
}

document.addEventListener("DOMContentLoaded", () => new AutoHideHeader());

// Add this to your screen.js file

class CoreCompatibleMobileSubmenuFix {
  constructor() {
    this.init();
  }

  init() {
    // Wait for WordPress Interactivity API to load
    document.addEventListener("DOMContentLoaded", () => {
      setTimeout(() => {
        this.setupMobileSubmenuFix();
      }, 100);
    });

    // Watch for mobile menu state changes
    this.observeMobileMenuChanges();
  }

  observeMobileMenuChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class" &&
          mutation.target.classList.contains(
            "wp-block-navigation__responsive-container",
          )
        ) {
          if (mutation.target.classList.contains("is-menu-open")) {
            setTimeout(() => {
              this.setupMobileSubmenuFix();
            }, 50);
          }
        }
      });
    });

    document
      .querySelectorAll(".wp-block-navigation__responsive-container")
      .forEach((container) => {
        observer.observe(container, {
          attributes: true,
          attributeFilter: ["class"],
        });
      });
  }

  setupMobileSubmenuFix() {
    const mobileContainer = document.querySelector(
      ".wp-block-navigation__responsive-container.is-menu-open",
    );
    if (!mobileContainer) return;

    // Find all submenu items in mobile view
    const submenuItems = mobileContainer.querySelectorAll(
      'li[data-wp-interactive="core/navigation"].has-child',
    );

    submenuItems.forEach((item) => {
      if (item.hasAttribute("data-mobile-submenu-fixed")) return;
      item.setAttribute("data-mobile-submenu-fixed", "true");

      // Get the toggle button and submenu container
      const toggleButton = item.querySelector(
        ".wp-block-navigation-submenu__toggle",
      );
      const submenuContainer = item.querySelector(
        ".wp-block-navigation__submenu-container",
      );

      if (!toggleButton || !submenuContainer) return;

      // Override the WordPress Interactivity API context for this item
      this.overrideInteractivityContext(item);

      // Add our custom click handler that preserves other submenus
      toggleButton.addEventListener(
        "click",
        (e) => {
          e.stopPropagation();

          // Get current state
          const isCurrentlyOpen = item.classList.contains(
            "mobile-submenu-open",
          );

          // Toggle this submenu without affecting others
          if (isCurrentlyOpen) {
            item.classList.remove("mobile-submenu-open");
            toggleButton.setAttribute("aria-expanded", "false");
          } else {
            item.classList.add("mobile-submenu-open");
            toggleButton.setAttribute("aria-expanded", "true");
          }
        },
        true,
      ); // Use capture to ensure we get the event first

      // Prevent WordPress from handling this click
      toggleButton.addEventListener("click", (e) => {
        e.preventDefault();
      });
    });

    // Override the global navigation store if available
    this.overrideNavigationStore();
  }

  overrideInteractivityContext(submenuItem) {
    try {
      // Get the current context
      const contextAttr = submenuItem.getAttribute("data-wp-context");
      if (contextAttr) {
        const context = JSON.parse(contextAttr);

        // Modify the context to prevent auto-closing
        context.submenuOpenedBy = {
          click: false,
          hover: false,
          focus: false,
        };
        context.type = "mobile-submenu"; // Custom type to differentiate

        submenuItem.setAttribute("data-wp-context", JSON.stringify(context));
      }
    } catch (e) {
      console.warn("Could not override interactivity context:", e);
    }
  }

  overrideNavigationStore() {
    // If WordPress Interactivity API is available, override specific actions
    if (window.wp && window.wp.interactivity) {
      const originalStore = window.wp.interactivity.getStore;

      if (originalStore) {
        try {
          const store = originalStore("core/navigation");
          if (store && store.actions) {
            // Override closeMenu to not close mobile submenus
            const originalCloseMenu = store.actions.closeMenu;
            store.actions.closeMenu = function (menuClosedOn = "click") {
              const context = window.wp.interactivity.getContext();

              // Don't close if we're in mobile submenu context
              if (context && context.type === "mobile-submenu") {
                return;
              }

              // Call original for other cases
              if (originalCloseMenu) {
                return originalCloseMenu.call(this, menuClosedOn);
              }
            };

            // Override toggleMenuOnClick for mobile
            const originalToggleMenu = store.actions.toggleMenuOnClick;
            store.actions.toggleMenuOnClick = function () {
              const context = window.wp.interactivity.getContext();
              const element = window.wp.interactivity.getElement();

              // If we're in mobile view, use our custom logic
              if (
                element.ref.closest(
                  ".wp-block-navigation__responsive-container.is-menu-open",
                )
              ) {
                // Let our custom handler deal with it
                return;
              }

              // Use original for desktop
              if (originalToggleMenu) {
                return originalToggleMenu.call(this);
              }
            };
          }
        } catch (e) {
          console.warn("Could not override navigation store:", e);
        }
      }
    }
  }
}

// Initialize the fix
new CoreCompatibleMobileSubmenuFix();

// document.addEventListener('DOMContentLoaded', function () {
//     const startTime = performance.now();
//     const video = document.querySelector('.wp-block-cover__video-background');
//
//     if (!video) return;
//
//     let hasReported = false;
//
//     function reportVideoReady() {
//         if (hasReported) return;
//
//         const readyTime = performance.now();
//         const loadDuration = readyTime - startTime;
//         console.log(`Video is ready to play after ${Math.round(loadDuration)} ms`);
//
//         hasReported = true;
//         video.removeEventListener('canplaythrough', reportVideoReady);
//
//         // Optional: send to analytics
//         // fetch('/track', { method: 'POST', body: JSON.stringify({ loadDuration }) });
//     }
//
//     video.addEventListener('canplaythrough', reportVideoReady);
//
//     // Fallback in case it already buffered before listener attached
//     if (video.readyState >= 4) { // HAVE_ENOUGH_DATA
//         reportVideoReady();
//     }
// });
